import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Building2, ImagePlus, Trash2, Clock, Phone, MapPin, ArrowLeft, Save, Star, BookOpen, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function Section({ icon: Icon, title, children, testid }) {
  return (
    <Card className="border-border" data-testid={testid}>
      <CardContent className="py-4 space-y-3">
        <p className="text-sm font-semibold flex items-center gap-1.5"><Icon className="h-4 w-4 text-fuchsia-400" /> {title}</p>
        {children}
      </CardContent>
    </Card>
  );
}

export default function VenueManagePage() {
  const { city, venueSlug } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const uploadTarget = useRef('gallery');

  useEffect(() => {
    axios.get(`${API}/venue-profiles/${city}/${venueSlug}/manage`, auth())
      .then(r => setForm(r.data))
      .catch(e => {
        toast.error(e.response?.data?.detail || 'You cannot edit this venue');
        navigate(`/${city}/venues/${venueSlug}`);
      });
  }, [city, venueSlug, navigate]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setHours = useCallback((day, field, value) => {
    setForm(f => ({ ...f, operating_hours: { ...f.operating_hours, [day]: { ...(f.operating_hours?.[day] || {}), [field]: value } } }));
  }, []);

  const pickFile = (target) => { uploadTarget.current = target; fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload`, fd, auth());
      const url = `${API}/files/${r.data.path}`;
      setForm(f => uploadTarget.current === 'cover'
        ? { ...f, cover_photo: url }
        : { ...f, photos: [...(f.photos || []), url] });
      toast.success('Photo uploaded — remember to save');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error('Venue name is required');
    setSaving(true);
    try {
      await axios.put(`${API}/venue-profiles/${city}/${venueSlug}/manage`, form, auth());
      toast.success('Venue page updated — changes are live');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="min-h-screen bg-background pt-28 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={onFile} data-testid="venue-photo-input" />
      <div className="max-w-3xl mx-auto px-4 space-y-4" data-testid="venue-manage-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <button onClick={() => navigate(`/${city}/venues/${venueSlug}`)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1" data-testid="back-to-venue">
              <ArrowLeft className="h-3 w-3" /> Back to public page
            </button>
            <h1 className="text-2xl font-extrabold flex items-center gap-2"><Building2 className="h-6 w-6 text-fuchsia-400" /> Manage {form.name}</h1>
            <p className="text-sm text-muted-foreground">Edits publish instantly to your public venue page.</p>
          </div>
          <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" disabled={saving} onClick={save} data-testid="venue-save-top">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save changes</>}
          </Button>
        </div>

        <Section icon={ImagePlus} title="Photos" testid="venue-edit-photos">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Cover photo (shown first on your page)</p>
            <div className="flex items-center gap-3">
              {form.cover_photo
                ? <img src={form.cover_photo} alt="cover" className="h-20 w-32 object-cover rounded-lg border border-border" />
                : <div className="h-20 w-32 rounded-lg border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">No cover</div>}
              <Button size="sm" variant="outline" disabled={uploading} onClick={() => pickFile('cover')} data-testid="upload-cover-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Upload cover'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground pt-1">Gallery</p>
            <div className="flex flex-wrap gap-2">
              {(form.photos || []).map((p, i) => (
                <div key={i} className="relative group">
                  <img src={p} alt="" className="h-20 w-28 object-cover rounded-lg border border-border" />
                  <button onClick={() => setForm(f => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                    className="absolute -top-1.5 -right-1.5 bg-rose-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`remove-photo-${i}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
              <button onClick={() => pickFile('gallery')} disabled={uploading}
                className="h-20 w-28 rounded-lg border border-dashed border-fuchsia-500/40 flex items-center justify-center text-fuchsia-300 hover:bg-fuchsia-500/10"
                data-testid="add-gallery-photo">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </Section>

        <Section icon={Star} title="About" testid="venue-edit-about">
          <div className="grid sm:grid-cols-2 gap-2">
            <Input placeholder="Venue name *" value={form.name || ''} onChange={set('name')} data-testid="venue-name-input" />
            <Input placeholder="Venue type (brewery, music hall…)" value={form.venue_type || ''} onChange={set('venue_type')} data-testid="venue-type-input" />
          </div>
          <Textarea placeholder="Describe your venue — vibe, history, what makes it special" rows={5}
            value={form.description || ''} onChange={set('description')} data-testid="venue-description-input" />
          <Input placeholder="Amenities, comma separated (patio, full bar, stage…)"
            value={(form.amenities || []).join(', ')}
            onChange={e => setForm(f => ({ ...f, amenities: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
            data-testid="venue-amenities-input" />
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" placeholder="Capacity min" value={form.capacity_min ?? ''} onChange={set('capacity_min')} data-testid="venue-capmin-input" />
            <Input type="number" placeholder="Capacity max" value={form.capacity_max ?? ''} onChange={set('capacity_max')} data-testid="venue-capmax-input" />
          </div>
        </Section>

        <Section icon={Clock} title="Hours" testid="venue-edit-hours">
          <p className="text-xs text-muted-foreground -mt-1">Leave "open" blank for closed days. Example: 11:00 AM / 10:00 PM</p>
          {DAYS.map(day => (
            <div key={day} className="grid grid-cols-[90px_1fr_1fr] gap-2 items-center">
              <span className="text-sm capitalize text-muted-foreground">{day.slice(0, 3)}</span>
              <Input placeholder="Open" value={form.operating_hours?.[day]?.open || ''}
                onChange={e => setHours(day, 'open', e.target.value)} data-testid={`hours-${day}-open`} />
              <Input placeholder="Close" value={form.operating_hours?.[day]?.close || ''}
                onChange={e => setHours(day, 'close', e.target.value)} data-testid={`hours-${day}-close`} />
            </div>
          ))}
        </Section>

        <Section icon={BookOpen} title="Menus" testid="venue-edit-menus">
          <p className="text-xs text-muted-foreground -mt-1">Link food, drink or event menus — they appear in a Menus tab on your page.</p>
          {(form.menus || []).map((m, i) => (
            <div key={i} className="rounded-lg border border-border p-3 space-y-2">
              <div className="grid sm:grid-cols-2 gap-2">
                <Input placeholder="Menu name (Dinner, Drinks…)" value={m.name || ''}
                  onChange={e => setForm(f => ({ ...f, menus: f.menus.map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))}
                  data-testid={`menu-name-${i}`} />
                <Input placeholder="Link URL (https://…)" value={m.url || ''}
                  onChange={e => setForm(f => ({ ...f, menus: f.menus.map((x, j) => j === i ? { ...x, url: e.target.value } : x) }))}
                  data-testid={`menu-url-${i}`} />
              </div>
              <div className="flex gap-2">
                <Input placeholder="Short description (optional)" value={m.description || ''}
                  onChange={e => setForm(f => ({ ...f, menus: f.menus.map((x, j) => j === i ? { ...x, description: e.target.value } : x) }))}
                  data-testid={`menu-desc-${i}`} />
                <Button size="sm" variant="outline" className="text-rose-400 border-rose-500/40 hover:bg-rose-500/10"
                  onClick={() => setForm(f => ({ ...f, menus: f.menus.filter((_, j) => j !== i) }))} data-testid={`menu-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            onClick={() => setForm(f => ({ ...f, menus: [...(f.menus || []), { name: '', url: '', description: '' }] }))}
            data-testid="add-menu-btn">+ Add a menu</Button>
        </Section>

        <Section icon={Sparkles} title="Private hire" testid="venue-edit-private-hire">
          <p className="text-xs text-muted-foreground -mt-1">Fill this in to show a Private Hire tab with a "request a quote" button on your page. Leave blank to hide it.</p>
          <Textarea placeholder="Describe your private hire offering — spaces, minimums, what's included" rows={4}
            value={form.private_hire?.description || ''}
            onChange={e => setForm(f => ({ ...f, private_hire: { ...(f.private_hire || {}), description: e.target.value } }))}
            data-testid="private-hire-desc" />
          <Input placeholder="Private hire capacity (e.g. up to 150 seated / 300 standing)"
            value={form.private_hire?.capacity || ''}
            onChange={e => setForm(f => ({ ...f, private_hire: { ...(f.private_hire || {}), capacity: e.target.value } }))}
            data-testid="private-hire-capacity" />
        </Section>

        <Section icon={Phone} title="Contact & links" testid="venue-edit-contact">
          <div className="grid sm:grid-cols-2 gap-2">
            <Input placeholder="Phone" value={form.phone || ''} onChange={set('phone')} data-testid="venue-phone-input" />
            <Input placeholder="Public email" value={form.public_email || ''} onChange={set('public_email')} data-testid="venue-email-input" />
            <Input placeholder="Website (https://…)" value={form.website || ''} onChange={set('website')} data-testid="venue-website-input" />
            <Input placeholder="Instagram URL or @handle" value={form.instagram || ''} onChange={set('instagram')} data-testid="venue-instagram-input" />
            <Input placeholder="Facebook URL" value={form.facebook || ''} onChange={set('facebook')} data-testid="venue-facebook-input" />
          </div>
        </Section>

        <Section icon={MapPin} title="Address & location" testid="venue-edit-location">
          <Input placeholder="Street address" value={form.address || ''} onChange={set('address')} data-testid="venue-address-input" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="State" value={form.state || ''} onChange={set('state')} data-testid="venue-state-input" />
            <Input placeholder="ZIP code" value={form.zip_code || ''} onChange={set('zip_code')} data-testid="venue-zip-input" />
          </div>
        </Section>

        <Button className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white" disabled={saving} onClick={save} data-testid="venue-save-bottom">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save & publish changes</>}
        </Button>
      </div>
    </div>
  );
}
